import {
  useCreateZone,
  useDeleteZone,
  useSetZoneTableLayout,
  useTables,
  useUpdateZone,
  useZones,
} from '@/features/reservation-setup/hooks/useReservationSetup';
import { zodResolver } from '@hookform/resolvers/zod';
import type { RestaurantTable, Zone } from '@restaurant/api-client';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import PushSheet from '@/components/PushSheet';
import { useT } from '@/i18n';
import {
  Block,
  BlockTitle,
  Button,
  Link as F7Link,
  List,
  ListInput,
  ListItem,
  Navbar,
  NavRight,
  Page,
  PageContent,
  Preloader,
  Stepper,
  Toolbar,
  f7,
} from 'framework7-react';

// ─── Add Zone sheet ───────────────────────────────────────────────────────────

const ZoneSchema = z.object({
  label: z
    .string()
    .min(1, 'String must contain at least 1 character(s)')
    .max(60, 'String must contain at most 60 character(s)'),
});
type ZoneForm = z.infer<typeof ZoneSchema>;

function AddZoneSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useT();
  const rs = t.reservationSetup;
  const createZone = useCreateZone();
  // Synchronous guard so a rapid double-tap on Add can't create two zones (the
  // link is opacity-styled, not a real disabled button).
  const submittingRef = useRef(false);
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ZoneForm>({
    resolver: zodResolver(ZoneSchema),
  });

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  const submit = handleSubmit((data) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    createZone.mutate(
      { label: data.label, sortOrder: 0 },
      {
        onSuccess: () => {
          f7.sheet.close();
          f7.toast.show({ text: rs.zoneAdded, closeTimeout: 2000 });
        },
        onError: (e) => f7.dialog.alert(e instanceof Error ? e.message : rs.failed),
        onSettled: () => {
          submittingRef.current = false;
        },
      },
    );
  });

  return (
    <PushSheet open={open} onClose={onClose} style={{ height: 'auto' }}>
      <Toolbar>
        <div className="left">
          <F7Link sheetClose>{t.cancel}</F7Link>
        </div>
        <div className="title">{rs.newZone}</div>
        <div className="right">
          <F7Link onClick={submit} style={{ opacity: createZone.isPending ? 0.4 : 1 }}>
            {rs.add}
          </F7Link>
        </div>
      </Toolbar>
      <PageContent>
        <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
          <Controller
            control={control}
            name="label"
            render={({ field }) => (
              <ListInput
                label={rs.zoneName}
                placeholder={rs.zoneNameExample}
                value={field.value ?? ''}
                outline={f7.theme !== 'ios'}
                floatingLabel={f7.theme !== 'ios'}
                onInput={(e) => field.onChange((e.target as HTMLInputElement).value)}
                onBlur={field.onBlur}
                errorMessage={errors.label?.message}
                errorMessageForce={!!errors.label?.message}
              />
            )}
          />
        </List>
      </PageContent>
    </PushSheet>
  );
}

// ─── Rename Zone sheet ────────────────────────────────────────────────────────

function RenameZoneSheet({
  open,
  zone,
  onClose,
}: {
  open: boolean;
  zone: Zone | null;
  onClose: () => void;
}) {
  const t = useT();
  const rs = t.reservationSetup;
  const updateZone = useUpdateZone();
  // Synchronous guard against a rapid double-tap (the link is opacity-styled).
  const submittingRef = useRef(false);
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ZoneForm>({
    resolver: zodResolver(ZoneSchema),
    defaultValues: { label: zone?.label ?? '' },
  });

  useEffect(() => {
    reset({ label: zone?.label ?? '' });
  }, [zone?.label, reset]);

  const submit = handleSubmit((data) => {
    if (!zone) return;
    if (submittingRef.current) return;
    submittingRef.current = true;
    updateZone.mutate(
      { zoneId: zone.id, body: { label: data.label } },
      {
        onSuccess: () => {
          f7.sheet.close();
          f7.toast.show({ text: rs.zoneRenamed, closeTimeout: 2000 });
        },
        onError: (e) => f7.dialog.alert(e instanceof Error ? e.message : rs.failed),
        onSettled: () => {
          submittingRef.current = false;
        },
      },
    );
  });

  return (
    <PushSheet open={open} onClose={onClose} style={{ height: 'auto' }}>
      <Toolbar>
        <div className="left">
          <F7Link sheetClose>{t.cancel}</F7Link>
        </div>
        <div className="title">{rs.renameZone}</div>
        <div className="right">
          <F7Link onClick={submit} style={{ opacity: updateZone.isPending ? 0.4 : 1 }}>
            {t.saveShort}
          </F7Link>
        </div>
      </Toolbar>
      <PageContent>
        <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
          <Controller
            control={control}
            name="label"
            render={({ field }) => (
              <ListInput
                label={rs.zoneName}
                value={field.value ?? ''}
                outline={f7.theme !== 'ios'}
                floatingLabel={f7.theme !== 'ios'}
                onInput={(e) => field.onChange((e.target as HTMLInputElement).value)}
                onBlur={field.onBlur}
                errorMessage={errors.label?.message}
                errorMessageForce={!!errors.label?.message}
              />
            )}
          />
        </List>
      </PageContent>
    </PushSheet>
  );
}

// ─── Add Tables sheet ─────────────────────────────────────────────────────────
//
// Pattern: the <Toolbar> (a "fixed" sheet element f7-react positions specially)
// stays a direct child of <PushSheet>; the interactive form lives in a *child*
// component (`*Content`) which exposes its submit handler via `submitRef`. A
// state change inside the child re-renders only the child - not f7-react's
// <Sheet> - so the open, push-transformed sheet isn't thrashed.

type Category = { seats: number; count: number };

function AddTablesSheet({
  open,
  zoneId,
  zoneName,
  currentCategories,
  onClose,
}: {
  open: boolean;
  zoneId: string;
  zoneName: string;
  currentCategories: Category[];
  onClose: () => void;
}) {
  const t = useT();
  const rs = t.reservationSetup;
  const submitRef = useRef<() => void>(() => {});
  return (
    <PushSheet open={open} onClose={onClose} style={{ height: 'auto' }}>
      <Toolbar>
        <div className="left">
          <F7Link sheetClose>{t.cancel}</F7Link>
        </div>
        <div className="title">{rs.addTableZone(zoneName)}</div>
        <div className="right">
          <F7Link onClick={() => submitRef.current()}>{rs.add}</F7Link>
        </div>
      </Toolbar>
      <AddTablesContent
        open={open}
        zoneId={zoneId}
        currentCategories={currentCategories}
        submitRef={submitRef}
      />
    </PushSheet>
  );
}

function AddTablesContent({
  open,
  zoneId,
  currentCategories,
  submitRef,
}: {
  open: boolean;
  zoneId: string;
  currentCategories: Category[];
  submitRef: { current: () => void };
}) {
  const rs = useT().reservationSetup;
  const setLayout = useSetZoneTableLayout();
  const [seats, setSeats] = useState(2);
  const [quantity, setQuantity] = useState(4);
  // Synchronous guard against a rapid double-tap on Add.
  const submittingRef = useRef(false);

  useEffect(() => {
    if (open) {
      setSeats(2);
      setQuantity(4);
    }
  }, [open]);

  submitRef.current = () => {
    if (submittingRef.current) return;
    const existing = currentCategories.find((c) => c.seats === seats);
    const merged: Category[] = existing
      ? currentCategories.map((c) => (c.seats === seats ? { ...c, count: c.count + quantity } : c))
      : [...currentCategories, { seats, count: quantity }].sort((a, b) => a.seats - b.seats);

    submittingRef.current = true;
    setLayout.mutate(
      { zoneId, body: { categories: merged } },
      {
        onSuccess: (res) => {
          f7.sheet.close();
          f7.toast.show({ text: rs.addedTables(res.created), closeTimeout: 2000 });
        },
        onError: (e) => f7.dialog.alert(e instanceof Error ? e.message : rs.failed),
        onSettled: () => {
          submittingRef.current = false;
        },
      },
    );
  };

  return (
    <PageContent>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListItem noChevron title={rs.seatsPerTable}>
          <Stepper
            slot="after"
            value={seats}
            min={1}
            max={20}
            onStepperChange={(v: number) => setSeats(v)}
            small
            raisedMd
          />
        </ListItem>
        <ListItem noChevron title={rs.numberOfTables}>
          <Stepper
            slot="after"
            value={quantity}
            min={1}
            max={99}
            onStepperChange={(v: number) => setQuantity(v)}
            small
            raisedMd
          />
        </ListItem>
      </List>
    </PageContent>
  );
}

// ─── Edit Tables sheet ────────────────────────────────────────────────────────

function EditTablesSheet({
  open,
  zone,
  seats,
  currentCount,
  currentCategories,
  onClose,
}: {
  open: boolean;
  zone: Zone;
  seats: number;
  currentCount: number;
  currentCategories: Category[];
  onClose: () => void;
}) {
  const t = useT();
  const rs = t.reservationSetup;
  const submitRef = useRef<() => void>(() => {});
  return (
    <PushSheet open={open} onClose={onClose} style={{ height: 'auto' }}>
      <Toolbar>
        <div className="left">
          <F7Link sheetClose>{t.cancel}</F7Link>
        </div>
        <div className="title">{rs.editSeatTables(seats, zone?.label)}</div>
        <div className="right">
          <F7Link onClick={() => submitRef.current()}>{t.saveShort}</F7Link>
        </div>
      </Toolbar>
      <EditTablesContent
        open={open}
        zone={zone}
        seats={seats}
        currentCount={currentCount}
        currentCategories={currentCategories}
        submitRef={submitRef}
      />
    </PushSheet>
  );
}

function EditTablesContent({
  open,
  zone,
  seats,
  currentCount,
  currentCategories,
  submitRef,
}: {
  open: boolean;
  zone: Zone;
  seats: number;
  currentCount: number;
  currentCategories: Category[];
  submitRef: { current: () => void };
}) {
  const rs = useT().reservationSetup;
  const setLayout = useSetZoneTableLayout();
  const [count, setCount] = useState(currentCount);
  // Synchronous guard against a rapid double-tap on Save.
  const submittingRef = useRef(false);

  // currentCount is 0 until a row is picked - sync from the prop when it OPENS.
  useEffect(() => {
    if (open) setCount(currentCount);
  }, [open, currentCount]);

  submitRef.current = () => {
    if (submittingRef.current) return;
    const categories: Category[] =
      count === 0
        ? currentCategories.filter((c) => c.seats !== seats)
        : currentCategories.some((c) => c.seats === seats)
          ? currentCategories.map((c) => (c.seats === seats ? { ...c, count } : c))
          : [...currentCategories, { seats, count }].sort((a, b) => a.seats - b.seats);

    submittingRef.current = true;
    setLayout.mutate(
      { zoneId: zone.id, body: { categories } },
      {
        onSuccess: () => {
          f7.sheet.close();
          f7.toast.show({ text: rs.tableUpdated, closeTimeout: 2000 });
        },
        onError: (e) => f7.dialog.alert(e instanceof Error ? e.message : rs.failed),
        onSettled: () => {
          submittingRef.current = false;
        },
      },
    );
  };

  return (
    <PageContent>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListItem noChevron title={rs.numberOfTables} footer={rs.setToDeactivateAll}>
          <Stepper
            slot="after"
            value={count}
            min={0}
            max={99}
            onStepperChange={(v: number) => setCount(v)}
            small
            raisedMd
          />
        </ListItem>
      </List>
    </PageContent>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

type SeatGroup = { seats: number; count: number };

function groupBySeat(tables: RestaurantTable[]): SeatGroup[] {
  const map = new Map<number, number>();
  for (const t of tables) map.set(t.seats, (map.get(t.seats) ?? 0) + 1);
  return Array.from(map.entries())
    .sort(([a], [b]) => a - b)
    .map(([seats, count]) => ({ seats, count }));
}

type TablesSheetState = { open: boolean; zoneId: string; zoneName: string };
type EditSheetState = { open: boolean; zone: Zone; seats: number; currentCount: number };

function TablesPage() {
  const t = useT();
  const rs = t.reservationSetup;
  const { data: zones, isLoading: zonesLoading } = useZones();
  const { data: tables, isLoading: tablesLoading } = useTables();
  const deleteZone = useDeleteZone();
  const setLayout = useSetZoneTableLayout();

  const [editMode, setEditMode] = useState(false);
  const [addZoneOpen, setAddZoneOpen] = useState(false);
  const [tablesSheet, setTablesSheet] = useState<TablesSheetState>({
    open: false,
    zoneId: '',
    zoneName: '',
  });
  const [editSheet, setEditSheet] = useState<EditSheetState>({
    open: false,
    zone: {} as Zone,
    seats: 2,
    currentCount: 0,
  });
  const [renameZone, setRenameZone] = useState<Zone | null>(null);
  const [renameOpen, setRenameOpen] = useState(false);

  const isLoading = zonesLoading || tablesLoading;

  const zoneTablesGrouped = (zoneId: string): SeatGroup[] =>
    groupBySeat((tables ?? []).filter((t) => t.zoneId === zoneId && t.isActive));

  // Tables whose zone was deleted keep living (Zone.onDelete = SetNull), so their
  // zoneId is null - or, defensively, points at a zone that no longer exists.
  // Without a bucket for them they'd vanish from this zone-grouped list while
  // still being real, bookable tables. Surface them under "No zone" so the
  // operator understands nothing was lost and can re-file them.
  const orphanGroups = (() => {
    const zoneIds = new Set((zones ?? []).map((z) => z.id));
    return groupBySeat(
      (tables ?? []).filter((t) => t.isActive && (!t.zoneId || !zoneIds.has(t.zoneId))),
    );
  })();

  const getActiveCategories = (zoneId: string): Category[] => {
    const map = new Map<number, number>();
    for (const t of tables ?? []) {
      if (t.zoneId === zoneId && t.isActive) {
        map.set(t.seats, (map.get(t.seats) ?? 0) + 1);
      }
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => a - b)
      .map(([seats, count]) => ({ seats, count }));
  };

  const openZoneActions = (zone: Zone) => {
    f7.actions
      .create({
        buttons: [
          [
            { text: zone.label, label: true },
            {
              text: rs.rename,
              onClick: () => {
                setRenameZone(zone);
                setRenameOpen(true);
              },
            },
            { text: rs.deleteZone, color: 'red', onClick: () => confirmDeleteZone(zone) },
          ],
          [{ text: t.cancel }],
        ],
      })
      .open();
  };

  const confirmDeleteZone = (zone: Zone) => {
    f7.dialog.confirm(rs.deleteZoneNoZoneConfirm(zone.label), rs.deleteZone, () => {
      deleteZone.mutate(
        { zoneId: zone.id },
        { onError: (e) => f7.dialog.alert(e instanceof Error ? e.message : rs.failed) },
      );
    });
  };

  const handleDeleteGroup = (group: SeatGroup, zone: Zone) => {
    f7.dialog.confirm(
      rs.removeAllTables(group.count, group.seats, zone.label),
      rs.removeTables,
      () => {
        const categories = getActiveCategories(zone.id).filter((c) => c.seats !== group.seats);
        setLayout.mutate(
          { zoneId: zone.id, body: { categories } },
          { onError: (e) => f7.dialog.alert(e instanceof Error ? e.message : rs.failed) },
        );
      },
    );
  };

  return (
    <Page name="tables">
      <Navbar title={rs.tables} backLink={t.back}>
        <NavRight>
          <F7Link
            onClick={() => setEditMode((e) => !e)}
            iconF7={editMode ? 'checkmark' : 'pencil'}
          />
        </NavRight>
      </Navbar>

      {isLoading && (
        <Block className="text-align-center">
          <Preloader />
        </Block>
      )}

      {!isLoading && zones?.length === 0 && (
        <Block strongIos strongMd outlineIos className="text-align-center">
          <p>{rs.noZones}</p>
          <Button fill round onClick={() => setAddZoneOpen(true)}>
            {rs.addAZone}
          </Button>
        </Block>
      )}

      {!isLoading &&
        zones?.map((zone) => {
          const groups = zoneTablesGrouped(zone.id);
          return (
            <div key={zone.id}>
              <BlockTitle>
                <span style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                  <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
                    {zone.label}
                  </span>
                  {editMode && (
                    <F7Link
                      iconF7="ellipsis_circle"
                      style={{ flexShrink: 0, marginLeft: 8 }}
                      onClick={() => openZoneActions(zone)}
                    />
                  )}
                </span>
              </BlockTitle>
              <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
                {groups.length === 0 && <ListItem title={rs.noTablesYet} />}
                {editMode && (
                  <ListItem>
                    <F7Link
                      slot="title"
                      onClick={() =>
                        setTablesSheet({ open: true, zoneId: zone.id, zoneName: zone.label })
                      }
                    >
                      {rs.addTablesLink}
                    </F7Link>
                  </ListItem>
                )}
                {groups.map((g) => (
                  <ListItem
                    key={g.seats}
                    title={rs.seatTables(g.seats)}
                    subtitle={rs.seatsTotal(g.seats, g.count)}
                    after={rs.tablesCount(g.count)}
                    onClick={
                      editMode
                        ? () =>
                            setEditSheet({
                              open: true,
                              zone,
                              seats: g.seats,
                              currentCount: g.count,
                            })
                        : undefined
                    }
                    style={{ cursor: editMode ? 'pointer' : 'default' }}
                  >
                    <i
                      slot="inner-start"
                      className="f7-icons"
                      onClick={
                        editMode
                          ? (e) => {
                              e.stopPropagation();
                              handleDeleteGroup(g, zone);
                            }
                          : undefined
                      }
                      style={{
                        color: 'var(--f7-color-red)',
                        fontSize: 20,
                        cursor: editMode ? 'pointer' : 'default',
                        opacity: editMode ? 1 : 0,
                        width: editMode ? 20 : 0,
                        overflow: 'hidden',
                        marginRight: editMode ? 6 : 0,
                        flexShrink: 0,
                        transition: 'opacity 0.2s ease, width 0.2s ease, margin-right 0.2s ease',
                        pointerEvents: editMode ? 'auto' : 'none',
                      }}
                    >
                      minus_circle_fill
                    </i>
                  </ListItem>
                ))}
              </List>
            </div>
          );
        })}

      {!isLoading && orphanGroups.length > 0 && (
        <div>
          <BlockTitle>{rs.noZoneLabel}</BlockTitle>
          <Block strongIos strongMd insetIos insetMd style={{ marginBottom: 0, paddingBottom: 0 }}>
            <p style={{ fontSize: 13, color: 'var(--f7-label-text-color, #8e8e93)', margin: 0 }}>
              {rs.orphanTablesNote}
            </p>
          </Block>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            {orphanGroups.map((g) => (
              <ListItem
                key={g.seats}
                title={rs.seatTables(g.seats)}
                subtitle={rs.seatsTotal(g.seats, g.count)}
                after={rs.tablesCount(g.count)}
              />
            ))}
          </List>
        </div>
      )}

      {!isLoading && editMode && (
        <List strongIos strongMd insetIos insetMd>
          <ListItem>
            <F7Link
              slot="title"
              onClick={() => setAddZoneOpen(true)}
              style={{ color: 'var(--f7-theme-color)' }}
            >
              {rs.addZone}
            </F7Link>
          </ListItem>
        </List>
      )}

      <AddZoneSheet open={addZoneOpen} onClose={() => setAddZoneOpen(false)} />

      <RenameZoneSheet open={renameOpen} zone={renameZone} onClose={() => setRenameOpen(false)} />

      <AddTablesSheet
        open={tablesSheet.open}
        zoneId={tablesSheet.zoneId}
        zoneName={tablesSheet.zoneName}
        currentCategories={getActiveCategories(tablesSheet.zoneId)}
        onClose={() => setTablesSheet((s) => ({ ...s, open: false }))}
      />

      <EditTablesSheet
        open={editSheet.open}
        zone={editSheet.zone}
        seats={editSheet.seats}
        currentCount={editSheet.currentCount}
        currentCategories={getActiveCategories(editSheet.zone?.id ?? '')}
        onClose={() => setEditSheet((s) => ({ ...s, open: false }))}
      />
    </Page>
  );
}

export default TablesPage;
